// React
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Components
import { MpsForm, MpsButton, MinimalFooter } from '@mobilitystore/components-interface';
import Loader from '../Shared/Utils/Loader';
import CustomizedSnackbars from '../Shared/Utils/SnackBar';
import LoanConsentModal from './consent-modal';
import LoanHeader from './loan-header';
import LoanOverview from './loan-overview';
import LoanStatusBanner from './loan-status-banner';

// Actions
import {
  createOrResumeLoanIntent,
  getLoanIntents,
  getLoanIntentStatus,
  getLoanUserConsents,
  getLoanUserRegistrationDetails,
  registerLoanUserDetails,
  saveLoanUserConsent
} from '../../actions/loan-application-action';
import { handleAAASignupLogin } from '../../actions/LoginAction';
import { setSnackbar } from '../../actions/generic-action';
import { handleFormInputChangeUtil, handleFormBlurUtil } from '../usp-landing/utils';
import { postLoginRedirectPathKey, formatMessage, getDeviceClass } from '../../utils';

// Data
import { loanApplicationFormFields, getLoanApplicationFormFields } from './data';
import idConstants from '../../data/idConstants';

// Assets
import { CDN_LINK, ROOT_CDN_LINK } from '../../assets/img';

// Css
import './index.scss';

const JUSPAY_PROVIDER = 'JUSPAY';
const MOCK_LENDER_ORG_ID = 'FLEXILOANS';
const REQUIRED_CONSENT_DOCUMENTS = ['DPN', 'TC'];
const NAME_FIELD_NAMES = ['firstName', 'lastName'];
const NON_ALPHA_SPACE_CHAR = /[^a-zA-Z ]/g;
const NON_DIGIT_CHAR = /[^0-9]/g;
const LOAN_USER_NOT_FOUND_ERROR_CODES = ['E000784', 'E000785'];

// Strips characters the field doesn't allow so invalid input can never be typed in, not just flagged on blur.
const sanitizeFieldValue = (name, value) => {
  if (typeof value !== 'string') return value;
  if (NAME_FIELD_NAMES.includes(name)) return value.replace(NON_ALPHA_SPACE_CHAR, '');
  if (name === 'phoneNumber') return value.replace(NON_DIGIT_CHAR, '');
  return value;
};

// A JUSPAY consent is only valid once both DPN and TC are present and accepted.
const hasValidJuspayConsent = (consentData) =>
  Boolean(
    consentData?.some(
      (consent) =>
        consent.name === JUSPAY_PROVIDER &&
        consent.consent &&
        REQUIRED_CONSENT_DOCUMENTS.every((document) => consent.documentNames?.includes(document))
    )
  );

const LoanApplication = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const deviceClass = getDeviceClass();
  const { content, isLoading, snackbarStatus, tenantConfig, user, validToken } = useSelector((state) => ({
    content: state.ContentReducer.content,
    isLoading: state.OrderSummaryReducer?.isLoading,
    snackbarStatus: state.GenericReducer.snackbarStatus,
    tenantConfig: state.TenantConfigReducer.tenantConfig,
    user: state.loginReducer.user,
    validToken: state.loginReducer.validToken
  }));

  const headerContent = content?.components?.header;
  const footerContent = content?.components?.footer;
  const loanContent = content?.components?.loans;
  const entityName = formatMessage(footerContent?.name, new Date().getFullYear());
  const loanFooterContent = loanContent?.footer;
  // Term-loan specific PDFs (not the app's own /data-protection-policy and /terms-of-use routes).
  const footerTabs = [
    loanFooterContent?.data_protection_policy && {
      id: 'data-protection-policy',
      name: loanFooterContent.data_protection_policy.name,
      link: `${ROOT_CDN_LINK}/${loanFooterContent.data_protection_policy.doc}`
    },
    loanFooterContent?.terms_of_use && {
      id: 'terms-of-use',
      name: loanFooterContent.terms_of_use.name,
      link: `${ROOT_CDN_LINK}/${loanFooterContent.terms_of_use.doc}`
    }
  ].filter(Boolean);
  const registrationFormContent = loanContent?.registration_form;
  const consentContent = loanContent?.consent;
  const applicationStatusContent = loanContent?.application_status;
  const errorContent = loanContent?.errors;

  const [formFields, setFormFields] = useState(loanApplicationFormFields);
  const [isProceedEnabled, setIsProceedEnabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registeredUserDetails, setRegisteredUserDetails] = useState(null);
  const [isConsentGiven, setIsConsentGiven] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const [loanIntentId, setLoanIntentId] = useState(null);
  const [loanStatusApplication, setLoanStatusApplication] = useState(null);
  const [requestedAmount, setRequestedAmount] = useState(null);
  const [isLoanFlowLoading, setIsLoanFlowLoading] = useState(false);
  const [isIntentActionLoading, setIsIntentActionLoading] = useState(false);
  const [loanFlowError, setLoanFlowError] = useState('');
  const [isLoanStatusUnavailable, setIsLoanStatusUnavailable] = useState(false);

  const logoBrandNameClickHandler = () => {
    history.push('/loans');
  };

  const handleLogin = () => {
    const { pathname, search = '', hash = '' } = history.location;
    sessionStorage.setItem(postLoginRedirectPathKey, `${pathname}${search}${hash}`);
    // Matches the seller login entry point elsewhere so IDP-gated dashboard checks stay correct.
    localStorage.setItem(idConstants.userLoginIdp, idConstants.idp.skid);
    handleAAASignupLogin();
  };

  const getFieldValue = (name) =>
    formFields.find((field) => field.name === name)?.value || '';

  // Re-derive the form field labels once the CDN content loads (initial state uses English defaults).
  useEffect(() => {
    if (!registrationFormContent?.fields) return;
    const labelledFields = getLoanApplicationFormFields(registrationFormContent.fields);
    setFormFields((currentFields) =>
      currentFields.map((field) => {
        const labelledField = labelledFields.find((item) => item.name === field.name);
        return labelledField ? { ...field, displayName: labelledField.displayName } : field;
      })
    );
  }, [registrationFormContent?.fields]);

  // On login, check registration + consent status up front so the right view renders immediately.
  useEffect(() => {
    if (!validToken) {
      setIsStatusLoading(false);
      return undefined;
    }

    let isMounted = true;

    const loadApplicantStatus = async () => {
      setIsStatusLoading(true);

      const [registrationResult, consentResult] = await Promise.allSettled([
        getLoanUserRegistrationDetails(),
        getLoanUserConsents()
      ]);

      if (!isMounted) return;

      const registrationErrorCode = registrationResult.reason?.response?.data?.error;

      if (registrationResult.status === 'fulfilled' && registrationResult.value?.data?.userId) {
        const registrationData = registrationResult.value.data;
        setIsRegistered(true);
        setRegisteredUserDetails(registrationData);
        setFormFields((currentFields) =>
          currentFields.map((field) => {
            if (field.name === 'firstName') {
              return { ...field, value: registrationData.firstName || '' };
            }
            if (field.name === 'lastName') {
              return { ...field, value: registrationData.lastName || '' };
            }
            if (field.name === 'email') {
              return { ...field, value: registrationData.emailId || '' };
            }
            if (field.name === 'phoneNumber') {
              return { ...field, value: registrationData.contactNo || '' };
            }
            return field;
          })
        );
      } else if (registrationResult.status === 'rejected' && !LOAN_USER_NOT_FOUND_ERROR_CODES.includes(registrationErrorCode)) {
        // Anything other than the documented "not registered" error codes is an unexpected failure.
        dispatch(
          setSnackbar({
            showSnackbar: true,
            message: errorContent?.fetch_user_details,
            severity: 'error'
          })
        );
      } else {
        setIsRegistered(false);
        // Pre-fill email from the auth session so returning users don't have to retype it, and
        // lock the field since an email id already exists for this account.
        if (user?.emailId) {
          setFormFields((currentFields) =>
            currentFields.map((field) =>
              field.name === 'email' && !field.value
                ? { ...field, value: user.emailId, disabled: true }
                : field
            )
          );
        }
      }

      if (consentResult.status === 'fulfilled') {
        setIsConsentGiven(hasValidJuspayConsent(consentResult.value?.data?.consentData));
      } else {
        dispatch(
          setSnackbar({
            showSnackbar: true,
            message: errorContent?.load_status,
            severity: 'error'
          })
        );
      }

      setIsStatusLoading(false);
    };

    loadApplicantStatus();

    return () => {
      isMounted = false;
    };
  }, [validToken, user?.emailId]);

  useEffect(() => {
    const mandatoryFieldsFilled = formFields.every(
      ({ validation, value, error }) =>
        !validation?.rule?.required?.isRequired ||
        (String(value ?? '').trim() && !error)
    );
    const hasAnyErrors = formFields.some((field) => field.error);

    setIsProceedEnabled(mandatoryFieldsFilled && !hasAnyErrors);
  }, [formFields]);

  const handleFormInputChange = (data) => {
    setFormFields(handleFormInputChangeUtil({ ...data, value: sanitizeFieldValue(data.name, data.value) }, formFields));
    setSubmitError('');
  };

  const handleFormBlur = (data) => {
    setFormFields(handleFormBlurUtil(data, formFields));
  };

  const saveConsent = async () => {
    try {
      await saveLoanUserConsent(JUSPAY_PROVIDER);
      setIsConsentGiven(true);
      return true;
    } catch (error) {
      setSubmitError(
        error?.response?.data?.message ||
          error?.message ||
          errorContent?.save_consent
      );
      return false;
    }
  };

  const completeRegistration = async (payload) => {
    const registeredUser = await dispatch(registerLoanUserDetails(payload));
    // Registration failure already surfaces an error via the Snackbar.
    if (registeredUser) {
      setIsRegistered(true);
      setRegisteredUserDetails(registeredUser);
    }
    return Boolean(registeredUser);
  };

  const handleSubmit = async () => {
    if (!isProceedEnabled || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError('');

    const registrationPayload = {
      firstName: getFieldValue('firstName').trim(),
      lastName: getFieldValue('lastName').trim(),
      emailId: getFieldValue('email').trim(),
      contactNo: getFieldValue('phoneNumber').trim()
    };

    try {
      // Re-check consent at submit time since it may have changed since the page loaded.
      const { data } = await getLoanUserConsents();

      if (hasValidJuspayConsent(data?.consentData)) {
        setIsConsentGiven(true);
        await completeRegistration(registrationPayload);
      } else {
        // Hold the form data until the user accepts consent in the modal.
        setPendingRegistration(registrationPayload);
        setShowConsentModal(true);
      }
    } catch (error) {
      setSubmitError(
        error?.response?.data?.message ||
          error?.message ||
          errorContent?.verify_consent
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConsentAccept = async () => {
    setIsSubmitting(true);
    setSubmitError('');

    const consentSaved = await saveConsent();
    if (!consentSaved) {
      // Keep the modal open on failure so the user doesn't have to re-scroll the document.
      setIsSubmitting(false);
      return;
    }

    if (pendingRegistration) {
      await completeRegistration(pendingRegistration);
      setPendingRegistration(null);
    }

    setIsSubmitting(false);
    setShowConsentModal(false);
  };

  // Reopen the consent modal automatically whenever a registered user hasn't consented yet.
  useEffect(() => {
    if (isStatusLoading || !isRegistered) return;
    setShowConsentModal(!isConsentGiven);
  }, [isStatusLoading, isRegistered, isConsentGiven]);

  const refreshLoanIntentStatus = async (intentId) => {
    if (!intentId) return;

    try {
      const { data } = await getLoanIntentStatus(intentId);
      const matchedApplication = data?.applications?.find(
        (application) => application.lender_details?.org_id === MOCK_LENDER_ORG_ID
      );
      setLoanStatusApplication(matchedApplication || null);
      setRequestedAmount(data?.requested_amount ?? null);
      setIsLoanStatusUnavailable(false);
    } catch (error) {
      // Status lookup failed - show the banner in an empty state rather than a hard error screen.
      setLoanStatusApplication(null);
      setRequestedAmount(null);
      setIsLoanStatusUnavailable(true);
    }
  };

  // Once registration and consent are confirmed, resolve whether to kick off a new loan
  // intent with Juspay or show the status of an existing one.
  useEffect(() => {
    if (isStatusLoading || !isRegistered || !isConsentGiven) return undefined;

    let isMounted = true;

    const loadLoanIntentFlow = async () => {
      setIsLoanFlowLoading(true);
      setLoanFlowError('');

      try {
        const { data: intents } = await getLoanIntents();
        if (!isMounted) return;

        if (!intents?.length) {
          const { data: createdIntent } = await createOrResumeLoanIntent({});
          if (!isMounted) return;

          if (createdIntent?.url) {
            window.open(createdIntent.url, '_blank', 'noopener,noreferrer');
          }
          setLoanIntentId(createdIntent?.loanIntentId || null);
        } else {
          const currentLoanIntentId = intents[0].loanIntentId;
          setLoanIntentId(currentLoanIntentId);
          await refreshLoanIntentStatus(currentLoanIntentId);
        }
      } catch (error) {
        if (!isMounted) return;
        setLoanFlowError(
          error?.response?.data?.message || error?.message || errorContent?.load_loan_flow
        );
      } finally {
        if (isMounted) setIsLoanFlowLoading(false);
      }
    };

    loadLoanIntentFlow();

    return () => {
      isMounted = false;
    };
  }, [isStatusLoading, isRegistered, isConsentGiven]);

  // Refresh the banner when the user returns from the third-party Juspay tab.
  useEffect(() => {
    if (!loanIntentId) return undefined;

    const handleWindowFocus = () => refreshLoanIntentStatus(loanIntentId);
    window.addEventListener('focus', handleWindowFocus);

    return () => window.removeEventListener('focus', handleWindowFocus);
  }, [loanIntentId]);

  const handleLoanIntentAction = async () => {
    if (!loanIntentId || isIntentActionLoading) return;

    setIsIntentActionLoading(true);

    try {
      const { data } = await createOrResumeLoanIntent({ loanIntentId });
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      setLoanFlowError(
        error?.response?.data?.message ||
          error?.message ||
          errorContent?.continue_loan_intent
      );
    } finally {
      setIsIntentActionLoading(false);
    }
  };

  return (
    <div className={`loan-application ${deviceClass}`}>
      {isLoading && <Loader />}
      {tenantConfig?.header?.ribbon && (
        <div
          style={{
            backgroundImage: `url(${CDN_LINK}/${headerContent?.images?.ribbon?.image})`
          }}
          className='bosch-strip'
        />
      )}
      <LoanHeader
        logoImage={`${CDN_LINK}/${headerContent?.images?.logo?.image}`}
        logoAlt={headerContent?.images?.logo?.alt}
        onLogoClick={logoBrandNameClickHandler}
        isLoggedIn={validToken}
        onLoginClick={handleLogin}
        firstName={registeredUserDetails?.firstName}
        lastName={registeredUserDetails?.lastName}
        emailId={registeredUserDetails?.emailId}
        loginLabel={loanContent?.header?.login_button}
        userMenuIconAriaLabel={loanContent?.user_menu?.icon_aria_label}
      />

      {validToken ? (
        isStatusLoading ? (
          <div className='loan-application__status-loading'>
            <Loader />
          </div>
        ) : !isRegistered ? (
          <div className='get-in-touch-section loan-application__form-section'>
            <div className='loan-application-form'>
              <div className='get-in-touch-overview'>
                <h1>{registrationFormContent?.title}</h1>
                <p>{registrationFormContent?.description}</p>
              </div>

              <MpsForm
                className='mps-get-in-touch__form'
                fields={formFields}
                handleInputChange={handleFormInputChange}
                handleBlur={handleFormBlur}
              />

              {submitError && (
                <div className='loan-application-form__submit-error' role='alert'>
                  {submitError}
                </div>
              )}

              <div className='mps-get-in-touch__button-container'>
                <MpsButton
                  label={
                    isSubmitting
                      ? registrationFormContent?.submit_button_loading
                      : registrationFormContent?.submit_button
                  }
                  mode='primary'
                  disabled={!isProceedEnabled || isSubmitting}
                  onClick={handleSubmit}
                />
              </div>
            </div>
          </div>
        ) : !isConsentGiven ? (
          <div className='loan-application__guest-content'>
            <section className='loan-application__hero' aria-labelledby='loan-status-title'>
              <h1 id='loan-status-title'>{consentContent?.pending_title}</h1>
              <p>{consentContent?.pending_description}</p>

              {submitError && (
                <div className='loan-application-form__submit-error' role='alert'>
                  {submitError}
                </div>
              )}
            </section>
          </div>
        ) : (
          <div className='loan-application__guest-content'>
            {isLoanFlowLoading ? (
              <div className='loan-application__status-loading'>
                <Loader />
              </div>
            ) : loanFlowError ? (
              <section className='loan-application__hero' aria-labelledby='loan-status-title'>
                <h1 id='loan-status-title'>{applicationStatusContent?.error_title}</h1>
                <p>{loanFlowError}</p>
              </section>
            ) : loanStatusApplication || isLoanStatusUnavailable ? (
              <LoanStatusBanner
                application={loanStatusApplication}
                requestedAmount={requestedAmount}
                isActionLoading={isIntentActionLoading}
                onAction={handleLoanIntentAction}
                loanIntentId={loanIntentId}
                content={applicationStatusContent?.banner}
              />
            ) : (
              <section className='loan-application__hero' aria-labelledby='loan-status-title'>
                <h1 id='loan-status-title'>{applicationStatusContent?.in_progress_title}</h1>
                <p>{applicationStatusContent?.in_progress_description}</p>
              </section>
            )}
          </div>
        )
      ) : (
        <div className='loan-application__guest-content'>
          <LoanOverview
            className='loan-application__overview'
            header={loanContent?.overview?.header}
            description={loanContent?.overview?.description}
            gateText={loanContent?.overview?.gate_text}
            imageSrc={loanContent?.overview?.image}
            onLoginClick={handleLogin}
            onRegisterClick={handleLogin}
            loginLabel={loanContent?.overview?.login_button}
            registerPrompt={loanContent?.overview?.register_prompt}
            registerLabel={loanContent?.overview?.register_button}
          />
        </div>
      )}

      <LoanConsentModal
        isOpen={showConsentModal}
        isSubmitting={isSubmitting}
        onClose={() => {
          if (isSubmitting) return;
          setShowConsentModal(false);
        }}
        onAccept={handleConsentAccept}
        content={consentContent?.modal}
        documentUrl={
          loanFooterContent?.terms_of_use?.doc &&
          `${ROOT_CDN_LINK}/${loanFooterContent.terms_of_use.doc}`
        }
        dataProtectionDocumentUrl={
          loanFooterContent?.data_protection_policy?.doc &&
          `${ROOT_CDN_LINK}/${loanFooterContent.data_protection_policy.doc}`
        }
      />

      <MinimalFooter
        className='footer-component'
        footerTabs={footerTabs}
        entityName={entityName}
      />

      {snackbarStatus?.showSnackbar && (
        <CustomizedSnackbars
          severity={snackbarStatus?.severity}
          message={snackbarStatus?.message}
          changeState={() => dispatch(setSnackbar({ showSnackbar: false, message: '' }))}
        />
      )}
    </div>
  );
};

export default LoanApplication;

